import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { User } from '../../../models/user'
import { MessageMode, MessageModes } from '../../../pages/messaging/messaging/messageModes'
import { IonicModule } from '@ionic/angular'
import {
  IonicSelectableComponent,
  IonicSelectableItemTemplateDirective,
  IonicSelectableValueTemplateDirective,
} from 'ionic-selectable'
import { SearchUtils } from '../../../shared/utils/searchUtils'
import { Message } from '../message'
import { PrivateMessage } from '../../../models/privateMessage'
import {
  HtmlEditorService,
  ImageService,
  ImageSettingsModel,
  LinkService,
  QuickToolbarService,
  RichTextEditorAllModule,
  ToolbarService,
} from '@syncfusion/ej2-angular-richtexteditor'
import { format } from 'date-fns'
import { FileUploadService } from '../../../services/file-upload.service'
import { SmsMessage } from '../../../models/smsMessage'
import { ToasterService } from '../../notification/toaster.service'
import { MessageRecipients } from '../MessageRecipients'
import { Workgroup } from '../../../models/workgroup'
import { coerceToArray } from '../../../services/utils'
import { UsersService } from '../../../services/domain/users.service'

@Component({
  selector: 'app-message-editor',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    RichTextEditorAllModule,
    IonicSelectableComponent,
    IonicSelectableItemTemplateDirective,
    IonicSelectableValueTemplateDirective,
  ],
  templateUrl: './message-editor.component.html',
  styleUrls: ['./message-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ToolbarService, LinkService, HtmlEditorService, ImageService, QuickToolbarService],
})
export class MessageEditorComponent implements OnInit, OnChanges {
  private fileUploadService = inject(FileUploadService)
  private toaster = inject(ToasterService)
  private cdr = inject(ChangeDetectorRef)
  private usersService = inject(UsersService)

  @Input() show: boolean = true
  @Input() parentMode: MessageMode
  @Input() users: User[]
  @Input() currentUser: User

  @Output() closeClick = new EventEmitter<void>()
  @Output() sendMessage = new EventEmitter<Message | SmsMessage>()

  @ViewChild('fileInput') fileInput: ElementRef
  @ViewChild('recipientSelect') recipientSelect: IonicSelectableComponent

  mode: MessageMode
  validationErrors: Set<string> = new Set<string>()

  msgSubject = ''
  msgBody = ''
  msgRecipients: MessageRecipients = null
  msgParent: PrivateMessage = null
  msgAttachmentUri = null
  msgAttachmentName = null

  allowedRecipients: MessageRecipients[] = []

  public tools: object = {
    type: 'MultiRow',
    enableFloating: false,
    items: [
      'Undo',
      'Redo',
      '|',
      'Bold',
      'Italic',
      'Underline',
      'StrikeThrough',
      '|',
      'BackgroundColor',
      '|',
      'Formats',
      '|',
    ],
  }
  messageModes = MessageModes.MODES
  private TAG = 'MessageEditor'
  rteInsertImageSettings: ImageSettingsModel = {
    saveFormat: 'Base64',
  }

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    console.log('ngOnChanges', changes)
    for (const ch in changes) {
      if (!changes.hasOwnProperty(ch)) continue

      switch (ch) {
        case 'parentMode':
          if (!this.currentUser) return
          this.maybeChangeMode(this.parentMode)
          break
        case 'currentUser':
          this.messageModes = this.messageModes.filter((mode) => this.isModeAllowedForCurrentUser(mode))
          if (!this.parentMode) return
          this.maybeChangeMode(this.parentMode)
          break
        case 'show':
          if (!this.parentMode || !this.currentUser) return
          this.maybeChangeMode(this.parentMode)
          break
        case 'users':
          this.allowedRecipients = this.usersToAllowedRecipients(this.users)
          break
        default:
          break
      }
    }
  }

  private maybeChangeMode(mode: MessageMode) {
    console.log('maybeChangeMode to', mode.name)
    if (this.isEditorEmpty() && this.isModeAllowedForCurrentUser(mode)) {
      console.log('maybeChangeMode 1')
      this.setMode(mode)
      return
    }
    if (!this.mode) {
      console.log('maybeChangeMode 2')
      this.setMode(this.getFirstAllowedModeForCurrentUser())
    }
  }

  private isEditorEmpty() {
    return !(
      this.msgBody ||
      this.msgSubject ||
      this.msgSubject?.length > 0 ||
      this.msgBody?.length > 0 ||
      this.msgRecipients !== null
    )
  }

  hide() {
    this.closeClick.emit()
  }

  searchRecipient(event: { component: IonicSelectableComponent; text: string }) {
    event.component.startSearch()

    const text = SearchUtils.normalizeForSearch(event.text)
    if (!text) event.component.items = this.allowedRecipients

    event.component.items = this.allowedRecipients.filter((recipient) => {
      return SearchUtils.normalizeForSearch(recipient.title).includes(text)
    })

    event.component.endSearch()
  }

  async send() {
    console.log(`${this.TAG}#send`, this)
    this.detectValidationErrors()
    console.log(this.TAG, 'validationErrors', this.validationErrors, this.validationErrors.size)
    if (this.validationErrors.size) return

    if (this.mode.hasRecipient) {
      for (const recipient of this.msgRecipients.users) {
        try {
          const feedbackMsg = this.msgParent?.assistance_feedback_message
          if (feedbackMsg) feedbackMsg.id = undefined

          this.sendMessage.emit(
            await MessageModes.createMessage(this.mode, this.usersService, {
              title: this.msgSubject,
              content: this.msgBody,
              publishedAt: new Date(),
              publishedByUser: this.currentUser,
              recipient,
              parentMessage: this.msgParent,
              assistance_feedback_message: feedbackMsg,
              isFeedback: !!feedbackMsg,
              attachmentUri: this.msgAttachmentUri,
              user: this.currentUser,
            }),
          )
          this.clearFileInput()
        } catch (e) {
          console.error('Sending message failed', e)
          this.toaster.simpleToast('Chyba při odesílání zprávy')
        }
      }
    } else {
      try {
        this.sendMessage.emit(
          await MessageModes.createMessage(this.mode, this.usersService, {
            title: this.msgSubject,
            content: this.msgBody,
            publishedAt: new Date(),
            publishedByUser: this.currentUser,
            recipient: null,
            parentMessage: this.msgParent,
            attachmentUri: this.msgAttachmentUri,
            isFeedback: false,
            user: this.currentUser,
          }),
        )
        this.clearFileInput()
      } catch (e) {
        console.error('Sending message failed', e)
        this.toaster.simpleToast('Chyba při odesílání zprávy')
      }
    }
  }

  public clearFileInput() {
    this.fileInput.nativeElement.files = null
    this.msgAttachmentName = null
    this.msgAttachmentUri = null
  }

  /**
   * Fills the editor window with content of a message to be replied to. Also fills the msgParent in.
   */
  public fillForReply(msg: Message, recipientOverride?: MessageRecipients) {
    this.msgRecipients = recipientOverride ? recipientOverride : new MessageRecipients(msg.publishedByUser)
    this.msgSubject = `Re: ${msg.title}`
    this.msgBody = `<br><blockquote><p>${msg.publishedByUser.fullName()}, ${format(msg.publishedAt, 'd. M. y, HH:mm')}:</p><p>${msg.content}</p></blockquote>`
    if (msg instanceof PrivateMessage) {
      this.msgParent = msg //.parentMessage ? msg.parentMessage : msg
    }
    this.cdr.detectChanges()
  }

  public clear() {
    this.msgRecipients = null
    this.msgParent = null
    this.msgSubject = ''
    this.msgBody = ''
    this.clearFileInput()
  }

  selectFile() {
    this.fileInput.nativeElement.click()
  }
  async onFileInput() {
    console.log('onfileinput', this.fileInput.nativeElement.files)

    if (this.fileInput.nativeElement.files.length > 0) {
      const documentUri = (await this.fileUploadService.upload(this.fileInput.nativeElement.files[0])).id
      this.msgAttachmentUri = documentUri
      this.msgAttachmentName = FileUploadService.parseFileNameFromUrl(documentUri)
      this.cdr.markForCheck()
    }
  }

  private isModeAllowedForCurrentUser(mode: MessageMode) {
    for (const r of mode.allowedRoles) {
      if (this.currentUser.getRoles().some((userRole) => userRole.valueOf() === r.valueOf())) {
        console.log('isModeAllowedForCurrentUser true', mode.name)
        return true
      }
    }
    console.log('isModeAllowedForCurrentUser false', mode.name)
    return false
  }

  private getFirstAllowedModeForCurrentUser() {
    console.log('getFirstAllowedModeForCurrentUser')
    for (const r of this.currentUser.getRoles()) {
      for (const m of MessageModes.MODES) {
        console.log(m, r)
        if (m.allowedRoles.some((allowedRole) => allowedRole.valueOf() === r.valueOf())) {
          return m
        }
      }
    }
  }

  private setMode(mode: MessageMode) {
    console.log('setMode', mode.name)
    this.mode = mode

    this.clearValidationErrors() // So that validation errors are not brought from one mode to another
  }

  private detectValidationErrors() {
    this.clearValidationErrors()

    if (this.mode.subjectAllowed && !this.msgSubject?.length) this.validationErrors.add('předmět není vyplněn')
    if (this.mode.hasRecipient && !this.msgRecipients) this.validationErrors.add('příjemce není vyplněn')
    if (!this.msgBody?.length) this.validationErrors.add('zpráva je prázdná')

    this.cdr.markForCheck()
  }

  clearValidationErrors() {
    this.validationErrors.clear()
    this.cdr.markForCheck()
  }

  onRecipientSelectClose() {
    if (this.recipientSelect?.isOpened) this.recipientSelect?.close()
  }

  private usersToAllowedRecipients(users: User[]): MessageRecipients[] {
    const allowedRecipients: MessageRecipients[] = []
    if (!users) return allowedRecipients

    // 1. get workgroups from users
    const workgroupsWithCoordinatorUsers: { wg: Workgroup; users: User[] }[] = []
    const coordinators = this.users.filter((u) => u.isOaCoordinator() && u?.workgroupId)
    for (const coo of coordinators) {
      const idx = workgroupsWithCoordinatorUsers.findIndex((wu) => wu.wg?.id === coo?.workgroupId)
      if (idx === -1) {
        workgroupsWithCoordinatorUsers.push({ wg: coo?.workgroup, users: [coo] })
      } else if (idx > -1) {
        workgroupsWithCoordinatorUsers[idx].users.push(coo)
      } else {
        console.debug('Ничего́')
      }
    }

    console.log(workgroupsWithCoordinatorUsers)

    // 2. fill allowedRecipients with workgroups
    allowedRecipients.push(
      ...workgroupsWithCoordinatorUsers
        .map((wu) => new MessageRecipients(wu.users, wu.wg.name))
        .sort((a, b) => {
          if (a.title > b.title) return 1
          return -1
        }),
    )

    // 3. fill allowedRecipients with coordinators
    allowedRecipients.push(
      ...coordinators
        .sort((a, b) => {
          if (a.workgroup.name > b.workgroup.name) return 1
          if (a.workgroup.name < b.workgroup.name) return -1

          if (a.fullName() > b.fullName()) return 1
          if (a.fullName() < b.fullName()) return -1

          return 0
        })
        .map((coo) => new MessageRecipients(coerceToArray(coo), `${coo.fullName()} (${coo?.workgroup.name})`)),
    )

    // 4. optionally for coordinators - fill allowedRecipients with assistants
    if (this.currentUser.isOaCoordinator()) {
      allowedRecipients.push(
        ...users
          .filter((u) => u.isOaAssistant() && !u.isOaCoordinator())
          .map((u) => new MessageRecipients(coerceToArray(u))),
      )
    }

    return allowedRecipients
  }
}
